import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import "./FloatingButtons.css";

const SpeechRecognitionImpl = typeof window !== "undefined" ?
  (window.SpeechRecognition || window.webkitSpeechRecognition) :
  null;

const ChatLayer = forwardRef(({ onUserInput, voiceMode }, ref) => {
  const [displayedText, setDisplayedText] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [allowTextInput, setAllowTextInput] = useState(false);
  const [userInput, setUserInput] = useState("");
  const [streamingText, setStreamingText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);


  const [compactText, setCompactText] = useState(false);
  const [alignTop, setAlignTop] = useState(false);
  const [overlayBackground, setOverlayBackground] = useState(false);
  const inputRef = useRef(null);
  const mountedRef = useRef(true);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);

  // Voice chat: mic input (STT) and spoken responses (TTS)
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  const utteranceRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      recognitionRef.current?.stop();
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Memoize styles to prevent re-renders
  const textContainerStyle = React.useMemo(() => ({
    // Responsive maxWidth for different screen sizes
    maxWidth: compactText
      ? "min(560px, 80vw)"
      : "min(800px, 85vw)",
    width: "100%",
    wordWrap: "break-word",
    // Add better text layout for larger screens
    margin: "0 auto",
    // Improve readability on larger screens
    fontSize: window.innerWidth > 1024 ? "1.25rem" : compactText ? "1.05rem" : "1.15rem",
    lineHeight: window.innerWidth > 1024 ? "1.65" : compactText ? "1.45" : "1.55",
    // Prevent layout shifts during typing
    minHeight: "200px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // Text stability
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    // Prevent text jumping
    transform: "translateZ(0)",
    backfaceVisibility: "hidden",
  }), [compactText]);

  // Memoize input container styles
  const inputContainerStyle = React.useMemo(() => ({
    // When input is enabled, use the pill container; otherwise, keep container minimal
    marginTop: "0.5rem",
    marginBottom: "calc(var(--safe-area-bottom) + 20px)",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    // Responsive width for different screen sizes
    width: allowTextInput
      ? window.innerWidth <= 430
        ? "85vw"
        : window.innerWidth <= 767
          ? "90vw"
          : "min(90vw, max(400px, min(600px, 50vw)))"
      : "auto",
    maxWidth: allowTextInput
      ? window.innerWidth <= 430
        ? "320px"
        : window.innerWidth <= 767
          ? "350px"
          : undefined
      : undefined,
    background: allowTextInput ? "rgba(0, 0, 0, 0.2)" : "transparent",
    border: "none",
    borderRadius: allowTextInput ? 50 : 0,
    boxShadow: allowTextInput
      ? "0 0 10px rgba(0, 255, 255, 0.5), 0 0 20px rgba(0, 255, 255, 0.3), 0 0 30px rgba(0, 255, 255, 0.1), inset 0 0 20px rgba(0, 255, 255, 0.1)"
      : "none",
    backdropFilter: allowTextInput ? "blur(10px)" : "none",
    padding: allowTextInput ? "4px 6px 4px 8px" : 0,
    // Make the "chat more" button float above everything
    position: allowTextInput ? "static" : "fixed",
    left: allowTextInput ? undefined : "50%",
    bottom: allowTextInput ? undefined : "calc(var(--safe-area-bottom) + 32px)",
    transform: allowTextInput ? undefined : "translateX(-50%)",
    zIndex: allowTextInput ? undefined : 10000,
    pointerEvents: "auto",
  }), [allowTextInput]);

  // Speak text aloud (TTS). Cancels any in-progress utterance first so a new
  // response always interrupts the previous one (no overlapping audio).
  const speak = (text) => {
    if (typeof window === "undefined" || !window.speechSynthesis || !text) return;
    window.speechSynthesis.cancel();
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.onstart = () => mountedRef.current && setIsSpeaking(true);
    utterance.onend = () => mountedRef.current && setIsSpeaking(false);
    utterance.onerror = () => mountedRef.current && setIsSpeaking(false);
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  };

  // Toggle mic listening (STT). Stops any TTS playback first so the app
  // doesn't talk over the user (barge-in).
  const toggleListening = () => {
    if (!SpeechRecognitionImpl) return;

    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }

    stopSpeaking();

    const recognition = new SpeechRecognitionImpl();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => mountedRef.current && setIsListening(true);
    recognition.onend = () => mountedRef.current && setIsListening(false);
    recognition.onerror = () => mountedRef.current && setIsListening(false);
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript?.trim();
      if (transcript && onUserInput) {
        onUserInput(transcript);
        setUserInput("");
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    speak,
    stopSpeaking,
    isVoiceInputSupported: () => !!SpeechRecognitionImpl,
    isVoiceOutputSupported: () => typeof window !== "undefined" && !!window.speechSynthesis,
    displayText: async (text) => {
      // In voice mode, speak the response aloud alongside the typed caption.
      if (voiceMode) {
        speak(text);
      }

      // Start streaming effect
      setStreamingText("");
      setIsStreaming(true);
      setShowInput(false);

      // Stream text word by word for smooth typing effect
      const words = text.split(' ');
      let currentText = "";

      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        currentText += (i > 0 ? ' ' : '') + word;
        setStreamingText(currentText);

        // Slower, more natural typing effect
        const baseDelay = 120; // Increased from 60ms
        const randomDelay = Math.random() * 80; // Increased from 40ms
        const punctuationDelay = /[.!?,;:]/.test(word) ? 400 : 0; // Pause on punctuation
        const sentenceDelay = /[.!?]/.test(word) ? 600 : 0; // Longer pause at sentence endings

        await new Promise(resolve => setTimeout(resolve, baseDelay + randomDelay + punctuationDelay + sentenceDelay));
      }

      // Set final text and show input
      setDisplayedText(text);
      setStreamingText("");
      setIsStreaming(false);
      setShowInput(true);

      // Focus the input with moderate delay
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 300);
    },
    clearText: () => {
      setDisplayedText("");
      setStreamingText("");
      setShowInput(false);
      setUserInput("");
      setIsStreaming(false);
    },
    // Clear immediately without typing animation
    resetForNewMessage: () => {
      setDisplayedText("");
      setStreamingText("");
      setShowInput(false);
      setUserInput("");
      setIsStreaming(false);
    },
    setAllowTextInput: (allow) => {
      setAllowTextInput(allow);
    },
    setCompactText: (compact) => {
      setCompactText(!!compact);
    },
    setAlignTop: (top) => {
      setAlignTop(!!top);
    },
    setOverlayBackground: (overlay) => {
      setOverlayBackground(!!overlay);
    },
    setSuggestedPrompts: (prompts) => {
      if (Array.isArray(prompts) && mountedRef.current) {
        setSuggestedPrompts(prompts);
      }
    },
    clearSuggestedPrompts: () => {
      setSuggestedPrompts([]);
    },
  }));

  const handleInputSubmit = (e) => {
    e.preventDefault();
    if (onUserInput) {
      onUserInput(userInput.trim());
      setUserInput("");
    }
  };

  return (
    <div
      className="chat-layer"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        color: "white",
        backgroundColor: overlayBackground ? "transparent" : "#A25D7B",
        // Prevent layout shifts
        position: "relative",
        overflow: "hidden",
        // Moderate transitions
        transition: "opacity 0.25s ease-in-out",
      }}
    >
      <style>
        {`
          @keyframes blink {
            0%, 50% { 
              opacity: 1; 
              box-shadow: 0 0 4px rgba(251, 242, 234, 0.6);
            }
            51%, 100% { 
              opacity: 0.3; 
              box-shadow: 0 0 2px rgba(251, 242, 234, 0.3);
            }
          }
        `}
      </style>
      {voiceMode && (isSpeaking || isListening) && (
        <button
          type="button"
          onClick={stopSpeaking}
          style={{
            position: "absolute",
            top: "calc(var(--safe-area-top, 0px) + 12px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10001,
            border: "none",
            borderRadius: 20,
            padding: "6px 16px",
            fontSize: "0.85rem",
            fontFamily: "'Nanum Myeongjo', 'Gowun Batang', serif",
            color: "#fbf2ea",
            background: "rgba(0, 0, 0, 0.35)",
            backdropFilter: "blur(6px)",
          }}
        >
          {isSpeaking ? "🔊 speaking — tap to stop" : "🎙️ listening..."}
        </button>
      )}
      {/* Displayed text area */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: alignTop ? "flex-start" : "center",
          fontSize: compactText ? "1.05rem" : "1.15rem",
          lineHeight: compactText ? "1.45" : "1.55",
          textAlign: "center",
          padding: compactText ? "0.5rem" : "1rem",
          color: "#fbf2ea",
          // Add responsive padding for larger screens
          paddingLeft: "max(1rem, calc((100vw - 800px) / 2))",
          paddingRight: "max(1rem, calc((100vw - 800px) / 2))",
        }}
      >
        <div
          className="chat-text-container"
          style={textContainerStyle}
        >
          {isStreaming ? (
            <div style={{
              display: 'inline',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {streamingText}
              <span
                className="typing-cursor"
                style={{
                  display: 'inline-block',
                  width: '2px',
                  height: '1.2em',
                  backgroundColor: '#fbf2ea',
                  marginLeft: '2px',
                  animation: 'blink 1s infinite',
                  verticalAlign: 'text-bottom',
                  boxShadow: '0 0 4px rgba(251, 242, 234, 0.6)',
                  borderRadius: '1px'
                }}
              />
            </div>
          ) : (
            displayedText
          )}
        </div>
      </div>

      {/* Input area (moved below text) */}
      {showInput && (
        <form
          className="chat-input-container"
          onSubmit={handleInputSubmit}
          style={inputContainerStyle}
        >
          {allowTextInput ? (
            <>
              <input
                ref={inputRef}
                type="text"
                value={userInput}
                onChange={(e) => {
                  setUserInput(e.target.value);
                }}
                onFocus={() => { }}
                onBlur={() => { }}
                placeholder="Type your response..."
                style={{
                  flex: 1,
                  padding: "0.6rem 0.8rem",
                  fontSize: "0.95rem",
                  fontFamily: "'Nanum Myeongjo', 'Gowun Batang', serif",
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  border: "none",
                  borderRadius: 30,
                  color: "#111",
                  outline: "none",
                  minWidth: 0,
                  boxSizing: "border-box",
                }}
              />
              <button type="submit" className="neon-button float-slow" style={{ fontFamily: "'Nanum Myeongjo', 'Gowun Batang', serif", border: "none" }}>
                Send
              </button>
              {voiceMode && SpeechRecognitionImpl && (
                <button
                  type="button"
                  className="neon-button float-slow"
                  onClick={toggleListening}
                  aria-label={isListening ? "Stop listening" : "Speak your response"}
                  style={{
                    border: "none",
                    minWidth: "50px",
                    height: "44px",
                    borderRadius: "50%",
                    fontFamily: "'Nanum Myeongjo', 'Gowun Batang', serif",
                    background: isListening ? "rgba(255, 80, 80, 0.6)" : undefined,
                    animation: isListening ? "blink 1s infinite" : "none",
                  }}
                >
                  {isListening ? "🛑" : "🎙️"}
                </button>
              )}
            </>
          ) : (
            <button
              type="submit"
              className="neon-button float-slow"
              style={{
                height: "50px",
                minWidth: "50px",
                padding: "0 20px",
                borderRadius: "50px",
                outline: "none",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              chat more
            </button>
          )}
        </form>
      )}

      {showInput && allowTextInput && suggestedPrompts.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            flexWrap: "wrap",
            justifyContent: "center",
            padding: "0 12px 12px",
          }}
        >
          {suggestedPrompts.map((p, idx) => (
            <button
              key={`${idx}-${p.slice(0, 20)}`}
              type="button"
              className="neon-button float-slow"
              style={{
                fontFamily: "'Nanum Myeongjo', 'Gowun Batang', serif",
                padding: "8px 14px",
                fontSize: "0.9rem",
                borderRadius: 24,
              }}
              onClick={() => {
                setUserInput(p);
                // Focus the input after state update
                setTimeout(() => {
                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }, 10);
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default ChatLayer;
